import styled, { keyframes } from 'styled-components';

import CheckImagePath from '../../image/Check.png';

// ============================================
// BulkSmsSection — 테이블 아래 케이스 카드 (전체 일괄 모드)
// ============================================

export function BulkSmsSection(props) {
  const { recommendation, onSend, theme } = props;
  const { templates, recipients, status } = recommendation;

  // 로딩 상태 //
  if (status === 'loading') {
    return (
      <BulkWrapper>
        <LoadingContent>
          <LoadingDots>
            <LoadingDot $color={theme.primary} style={{ animationDelay: '0ms' }} />
            <LoadingDot $color={theme.primary} style={{ animationDelay: '300ms' }} />
            <LoadingDot $color={theme.primary} style={{ animationDelay: '600ms' }} />
          </LoadingDots>
          <LoadingText $color={theme.subText}>문자 템플릿 준비 중...</LoadingText>
        </LoadingContent>
      </BulkWrapper>
    );
  }

  // 에러 //
  if (status === 'error') {
    return (
      <BulkWrapper>
        <ErrorText $color={theme.pointRed}>발송 실패 - 다시 시도해주세요</ErrorText>
      </BulkWrapper>
    );
  }

  if (!templates || templates.length === 0) return null;

  const recipientCount = recipients?.length || 0;
  const isFinished = status === 'completed';

  // 발송 //
  function onClickSend(tplId, templateText) {
    if (isFinished) return;
    const rawText = templateText.replace(/\[회원이름\]/g, '{member_name}').replace(/\[센터이름\]/g, '{center_name}');
    onSend(tplId, rawText, recipients);
  }

  // 템플릿별 버튼 라벨 + 스타일 결정 //
  function getButtonProps(tpl) {
    const tplStatus = tpl.status || 'ready';
    switch (tplStatus) {
      case 'sent':
        return { label: '발송 완료', disabled: true, gray: true };
      case 'unsent':
        return { label: '미발송', disabled: true, gray: true };
      case 'sending':
        return { label: '발송 중...', disabled: true, gray: false };
      default:
        return { label: `${recipientCount}명에게 문자 발송하기`, disabled: false, gray: false };
    }
  }

  return (
    <BulkWrapper>
      {templates.map((tpl, idx) => {
        const btnProps = getButtonProps(tpl);
        return (
          <CaseCard key={tpl.id}>
            <CaseCardInner>
              <CaseTab $bg={btnProps.gray ? theme.gray : theme.primary}>
                <CaseTabText $color={btnProps.gray ? theme.subText : theme.white}>추천 문자.{idx + 1}</CaseTabText>
              </CaseTab>
              <CaseBody $bg={btnProps.gray ? theme.gray + '0D' : theme.primary + '0D'}>
                <CaseMessageText $color={theme.text}>{displayPlaceholders(tpl.text)}</CaseMessageText>
              </CaseBody>
            </CaseCardInner>
            <SendButton
              onClick={() => onClickSend(tpl.id, tpl.text)}
              $disabled={btnProps.disabled}
              $bg={btnProps.gray ? theme.gray : theme.primary}
              $color={btnProps.gray ? theme.subText : theme.white}
            >
              {btnProps.label}
            </SendButton>
          </CaseCard>
        );
      })}
    </BulkWrapper>
  );
}

// ============================================
// InlineSmsCell — 테이블 셀 내 추천 문자 (개별 커스텀 모드)
// ============================================

export function InlineSmsCell(props) {
  const { memberId, individualTexts, onGenerate, theme } = props;
  const entry = individualTexts?.[memberId];
  const status = entry?.status;
  const text = entry?.text;

  // 아직 생성 안 됨 //
  if (!entry || !status) {
    return null;
  }

  // 로딩 중 //
  if (status === 'loading') {
    return (
      <InlineLoadingDots>
        <LoadingDot $color={theme.primary} style={{ animationDelay: '0ms' }} />
        <LoadingDot $color={theme.primary} style={{ animationDelay: '300ms' }} />
        <LoadingDot $color={theme.primary} style={{ animationDelay: '600ms' }} />
      </InlineLoadingDots>
    );
  }

  // 에러 //
  if (status === 'error') {
    return (
      <GenerateButton onClick={onGenerate} $bg={theme.pointRed} $color={theme.white}>
        재시도
      </GenerateButton>
    );
  }

  // 생성 완료 → 텍스트 표시 //
  return (
    <InlineMessageText $color={theme.text}>{text}</InlineMessageText>
  );
}

// ============================================
// InlineSmsSendCell — 발송 버튼 셀 (개별 커스텀 모드)
// ============================================

export function InlineSmsSendCell(props) {
  const { memberId, individualTexts, onGenerate, onSend, theme } = props;
  const entry = individualTexts?.[memberId];
  const status = entry?.status;
  const text = entry?.text;

  const isSent = status === 'sent';
  const isReady = status === 'ready' && text;

  // 미생성 / 에러 → 문자 생성 하기 버튼 //
  if (!entry || !status || status === 'error') {
    return (
      <GenerateButton onClick={onGenerate} $bg={theme.primary} $color={theme.white}>
        문자 생성 하기
      </GenerateButton>
    );
  }

  // 로딩 중 //
  if (status === 'loading') {
    return (
      <InlineLoadingDots>
        <LoadingDot $color={theme.primary} style={{ animationDelay: '0ms' }} />
        <LoadingDot $color={theme.primary} style={{ animationDelay: '300ms' }} />
        <LoadingDot $color={theme.primary} style={{ animationDelay: '600ms' }} />
      </InlineLoadingDots>
    );
  }

  // sending 상태 //
  if (status === 'sending') {
    return (
      <SentCheckWrapper $clickable={false}>
        <SentText $color={theme.text}>발송 중...</SentText>
      </SentCheckWrapper>
    );
  }

  // 그 외 대기 상태 //
  if (!isReady && !isSent) return null;

  // 발송 클릭 핸들러 //
  function onClickSend() {
    if (isSent) return;
    onSend(memberId, text);
  }

  return (
    <SentCheckWrapper onClick={onClickSend} $clickable={isReady}>
      <SentText $color={theme.text}>발송</SentText>
      {
        isSent ?
          <CheckIcon $bg={theme.subKey}>
            <CheckImage src={CheckImagePath} />
          </CheckIcon>
          :
          <GrayDot $color={theme.gray} />
      }
    </SentCheckWrapper>
  );
}

// ============================================
// 유틸
// ============================================

function displayPlaceholders(text) {
  if (!text) return '';
  return text
    .replace(/\{member_name\}/g, '[회원이름]')
    .replace(/\{center_name\}/g, '[센터이름]');
}

// ============================================
// Styled Components
// ============================================

const appearAnimation = keyframes`
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`;

// Bulk Section Styles //

const BulkWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 12px;

  animation: ${appearAnimation} 0.3s ease-out forwards;
`;

const CaseCard = styled.div`
  display: flex;
  flex-direction: column;

  gap: 5px;

  padding: 10px 0;
`;

const CaseCardInner = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const CaseTab = styled.div`
  display: flex;
  align-items: center;

  padding: 0 10px;

  background-color: ${props => props.$bg};

  border-radius: 5px 5px 0 0;

  align-self: flex-start;
`;

const CaseTabText = styled.span`
  font-family: NotoSansKR-Black;
  font-size: 14px;
  line-height: 25px;

  color: ${props => props.$color};

  text-align: center;
`;

const CaseBody = styled.div`
  display: flex;
  align-items: center;

  padding: 5px 10px;

  background-color: ${props => props.$bg};

  border-radius: 0 10px 10px 10px;

  width: 100%;

  box-sizing: border-box;
`;

const CaseMessageText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;
  line-height: 30px;

  color: ${props => props.$color};

  margin: 0;

  white-space: pre-wrap;

  flex: 1;
`;

const SendButton = styled.button`
  width: 100%;
  height: 40px;

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.$color};

  background-color: ${props => props.$bg};

  border: none;
  border-radius: 200px;

  cursor: ${props => props.$disabled ? 'default' : 'pointer'};

  opacity: ${props => props.$disabled ? 0.5 : 1};

  transition: opacity 0.15s ease;
`;

// Inline Cell Styles //

const GenerateButton = styled.button`
  padding: 6px 14px;

  font-family: NotoSansKR-Medium;
  font-size: 16px;

  color: ${props => props.$color};

  background-color: ${props => props.$bg};

  border: none;
  border-radius: 6px;

  cursor: ${props => props.$disabled ? 'default' : 'pointer'};

  opacity: ${props => props.$disabled ? 0.5 : 1};

  white-space: nowrap;

  transition: opacity 0.15s ease;
`;

const InlineLoadingDots = styled.div`
  display: flex;
  align-items: center;

  gap: 4px;

  padding: 6px 0;
`;

const InlineMessageText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 16px;
  line-height: 22px;

  color: ${props => props.$color};

  margin: 0;

  white-space: pre-wrap;

  max-width: 280px;
`;

const SentCheckWrapper = styled.div`
  display: flex;
  align-items: center;

  gap: 6px;

  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
`;

const SentText = styled.span`
  font-family: NotoSansKR-Medium;
  font-size: 16px;

  color: ${props => props.$color};
`;

const CheckIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 25px;
  height: 25px;

  border-radius: 25px;

  background-color: ${props => props.$bg};
`;

const CheckImage = styled.img`
  width: 12px;
  height: 12px;
`;

const GrayDot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 25px;
  height: 25px;

  border-radius: 25px;

  border: 2px dashed ${props => props.$color};

  box-sizing: border-box;
`;

// Common //

const LoadingContent = styled.div`
  display: flex;
  align-items: center;

  gap: 10px;

  padding: 8px 0;
`;

const LoadingDots = styled.div`
  display: flex;

  gap: 4px;
`;

const LoadingDot = styled.span`
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background-color: ${props => props.$color};

  animation: ${dotBounce} 1.4s infinite ease-in-out both;
`;

const LoadingText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 16px;

  color: ${props => props.$color};

  margin: 0;
`;

const ErrorText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 16px;

  color: ${props => props.$color};

  margin: 0;

  padding: 8px 0;
`;
